import { motion } from "framer-motion";
import {
  Code2,
  Globe,
  Smartphone,
  BarChart3,
  Brain,
  Cloud,
  Compass,
  ShieldCheck,
  Wrench,
  Users,
  Palette,
  Lock,
  Plug,
  Boxes,
} from "lucide-react";

const blocks = [
  {
    num: "01",
    icon: Code2,
    title: "Custom Software Development",
    items: [
      "Web applications — portals & dashboards",
      "Enterprise software — ERP, CRM",
      "SaaS product development",
      "Tailored solutions based on client needs",
    ],
  },
  {
    num: "02",
    icon: Globe,
    title: "Web Development",
    items: [
      "Business websites",
      "E-commerce platforms",
      "CMS-based sites (WordPress & headless)",
    ],
  },
  {
    num: "03",
    icon: Smartphone,
    title: "Mobile App Development",
    items: [
      "Native Android & iOS apps",
      "Cross-platform — Flutter, React Native",
      "App maintenance & upgrades",
    ],
  },
  {
    num: "04",
    icon: BarChart3,
    title: "Data & Analytics",
    items: [
      "Data analysis & reporting",
      "Dashboards — Power BI, Tableau",
      "Data engineering & ETL pipelines",
    ],
  },
  {
    num: "05",
    icon: Brain,
    title: "AI / Machine Learning",
    items: [
      "Chatbots & automation",
      "Predictive analytics",
      "AI tools & integrations",
    ],
  },
  {
    num: "06",
    icon: Cloud,
    title: "Cloud Services",
    items: [
      "AWS · Azure · GCP setup",
      "Cloud migration",
      "DevOps & CI/CD pipelines",
    ],
  },
  {
    num: "07",
    icon: Compass,
    title: "IT Consulting",
    items: [
      "Technology strategy",
      "System architecture design",
      "Digital transformation consulting",
    ],
  },
  {
    num: "08",
    icon: ShieldCheck,
    title: "Testing & QA",
    items: [
      "Manual testing",
      "Automation testing",
      "Performance & security testing",
    ],
  },
  {
    num: "09",
    icon: Wrench,
    title: "Maintenance & Support",
    items: ["Application support", "Bug fixing", "System upgrades"],
  },
  {
    num: "10",
    icon: Users,
    title: "Staffing / IT Services",
    items: [
      "Contract staffing — C2C, W2",
      "Dedicated developers",
      "Project-based hiring",
    ],
  },
];

const addOns = [
  { icon: Palette, label: "UI / UX Design" },
  { icon: Lock, label: "Cybersecurity Services" },
  { icon: Plug, label: "API Development & Integration" },
  { icon: Boxes, label: "Blockchain (on request)" },
];

export default function ServicesDetail() {
  return (
    <section
      id="capabilities"
      data-testid="services-detail-section"
      className="bg-white border-t fis-hairline"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16">
          <div className="md:col-span-7">
            <div className="font-mono-label mb-4">
              § 01·b — Full Capability Stack
            </div>
            <h2 className="font-display text-5xl md:text-7xl">
              End-to-end
              <br />
              <span className="text-[color:var(--fis-ink-soft)]">
                software solutions.
              </span>
            </h2>
          </div>
          <p className="md:col-span-5 text-[color:var(--fis-ink-soft)] leading-relaxed">
            We provide end-to-end software solutions including web & mobile
            development, AI solutions, data analytics, cloud services, and IT
            consulting. We specialize in building scalable, high-performance
            applications tailored to business needs.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l fis-hairline">
          {blocks.map((b, i) => {
            const Icon = b.icon;
            return (
              <motion.div
                key={b.num}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: (i % 3) * 0.05 }}
                data-testid={`capability-${b.num}`}
                className="border-r border-b fis-hairline p-8 md:p-10 group hover:bg-[color:var(--fis-bg-alt)] transition-colors"
              >
                <div className="flex items-start justify-between">
                  <span className="font-mono-label">{b.num}</span>
                  <Icon className="w-5 h-5 text-[color:var(--fis-ink-soft)] group-hover:text-[color:var(--fis-accent)] transition-colors" />
                </div>
                <h3 className="font-display text-2xl md:text-3xl mt-8">
                  {b.title}
                </h3>
                <ul className="mt-5 space-y-2 text-sm text-[color:var(--fis-ink-soft)]">
                  {b.items.map((item) => (
                    <li key={item} className="flex gap-2.5">
                      <span className="w-3 h-px bg-[color:var(--fis-ink)] mt-2.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-20">
          <div className="font-mono-label mb-6">Optional add-on services</div>
          <div className="flex flex-wrap gap-3">
            {addOns.map((a) => {
              const Icon = a.icon;
              return (
                <div
                  key={a.label}
                  data-testid={`addon-${a.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="inline-flex items-center gap-2.5 px-5 py-3 border fis-hairline hover:border-[color:var(--fis-accent)] hover:text-[color:var(--fis-accent)] transition-colors"
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm">{a.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
